import React from 'react';
import { Oval } from 'react-loader-spinner';
import toast from 'react-hot-toast';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';

import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { createPunchCard, resetCreatePunchCard } from '../../punchCardSlice';

import './CreatePunchCardForm.scss';

export interface CreatePunchCardFormProps {
  driverId: string;
}

export function CreatePunchCardForm({ driverId }: CreatePunchCardFormProps) {
  const dispatch = useAppDispatch();

  const createPunchCardAPI = useAppSelector(state => state.punchCards.createPunchCardAPI);

  const [date, setDate] = React.useState<moment.Moment | null>(moment());
  const [punchIn, setPunchIn] = React.useState<moment.Moment | null>(moment());
  const [punchOut, setPunchOut] = React.useState<moment.Moment | null>(null);

  React.useEffect(() => {
    return () => {
      dispatch(resetCreatePunchCard());
    }
  }, []);

  React.useEffect(() => {
    if (!createPunchCardAPI.loading) {
      if (!!createPunchCardAPI.error) {
        toast.error('Something went wrong. Please try again.');
      }

      if (!!createPunchCardAPI.value) {
        toast.success('Successfully created punch card.');
      }
    }
  }, [createPunchCardAPI.loading, !!createPunchCardAPI.error, !!createPunchCardAPI.value]);

  const onCreate = () => {
    if (!date || !punchIn) {
      toast.error('Date and punch in time are required.');
      return;
    }

    const dateStr = date.format('YYYY-MM-DD');
    const punchInDateTime = moment(`${dateStr} ${punchIn.format('HH:mm')}`);

    let punchOutStr: string | undefined;
    if (punchOut) {
      const punchOutDateTime = moment(`${dateStr} ${punchOut.format('HH:mm')}`);
      if (punchOutDateTime.isBefore(punchInDateTime)) {
        toast.error('Punch out must be after punch in!');
        return;
      }
      punchOutStr = punchOutDateTime.toISOString();
    }

    dispatch(createPunchCard({
      driverId,
      date: dateStr,
      punchIn: punchInDateTime.toISOString(),
      punchOut: punchOutStr
    }));
  };

  const onPunchOutChange = (newPunchOut: moment.Moment | null) => {
    if (!newPunchOut) {
      setPunchOut(null);
      return;
    }

    if (date && punchIn) {
      const dateStr = date.format('YYYY-MM-DD');
      const punchOutDateTime = moment(`${dateStr} ${newPunchOut.format('HH:mm')}`);
      const punchInDateTime = moment(`${dateStr} ${punchIn.format('HH:mm')}`);

      if (punchOutDateTime.isBefore(punchInDateTime)) {
        toast.error('Punch out must be after punch in!');
        return;
      }
    }

    setPunchOut(newPunchOut);
  }

  return (
    <div className='create-punch-card-form'>
      <div className='date-picker-container'>
        <DatePicker
          label='Date'
          value={date}
          onChange={setDate}
        />
      </div>

      <div className='time-picker-container'>
        <TimePicker
          label='Punch In'
          value={punchIn}
          onChange={setPunchIn}
        />
      </div>

      <div className='time-picker-container'>
        <TimePicker
          label='Punch Out (optional)'
          value={punchOut}
          onChange={onPunchOutChange}
        />
      </div>

      {createPunchCardAPI.loading
        ? <div className='submit-button'><Oval color="#131313" height={20} width={20} secondaryColor="#FBD20A"/></div>
        : <div className='submit-button' onClick={onCreate}>Create</div>
      }
    </div>
  );
}
